/** Canonical JSON: object keys sorted recursively (by UTF-16 code unit order), no whitespace. */
export function canonicalJson(value) {
    if (value === null || typeof value !== 'object') {
        if (typeof value === 'number' && !Number.isFinite(value))
            throw new Error('non-finite number');
        return JSON.stringify(value);
    }
    if (Array.isArray(value))
        return `[${value.map((v) => canonicalJson(v === undefined ? null : v)).join(',')}]`;
    const obj = value;
    const keys = Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(',')}}`;
}
/**
 * Catalog ordering key: UTF-8 bytes of the virtual path with every "/" replaced by 0x00. Compared with
 * memcmp this equals a depth-first walk with bytewise-sorted siblings (see docs/backup-format.md §7).
 */
export function pathKey(virtualPath) {
    const bytes = new TextEncoder().encode(virtualPath);
    for (let i = 0; i < bytes.length; i++)
        if (bytes[i] === 0x2f)
            bytes[i] = 0;
    return bytes;
}
export function comparePathKeys(a, b) {
    const n = Math.min(a.length, b.length);
    for (let i = 0; i < n; i++)
        if (a[i] !== b[i])
            return a[i] - b[i];
    return a.length - b.length;
}
export function parentPath(virtualPath) {
    if (virtualPath === '/')
        return '';
    const i = virtualPath.lastIndexOf('/');
    return i <= 0 ? '/' : virtualPath.slice(0, i);
}
export function baseName(virtualPath) {
    if (virtualPath === '/')
        return '/';
    return virtualPath.slice(virtualPath.lastIndexOf('/') + 1);
}
/** Validate a virtual path: absolute, no empty/./.. components, no NUL/backslash. */
export function isValidVirtualPath(p) {
    if (p === '/')
        return true;
    if (!p.startsWith('/') || p.endsWith('/') || p.includes('\\') || p.includes('\0'))
        return false;
    return p
        .slice(1)
        .split('/')
        .every((c) => c.length > 0 && c !== '.' && c !== '..');
}
/** Convert a virtual path to its display form for the given platform. */
export function displayPath(virtualPath, pathStyle) {
    if (pathStyle === 'posix')
        return virtualPath;
    if (virtualPath === '/')
        return 'This PC';
    const rest = virtualPath.slice(1);
    if (rest.startsWith('UNC/'))
        return '\\\\' + rest.slice(4).replaceAll('/', '\\');
    const [drive, ...tail] = rest.split('/');
    return tail.length === 0 ? `${drive}\\` : `${drive}\\${tail.join('\\')}`;
}
export function formatBytes(n) {
    if (!Number.isFinite(n))
        return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let v = Math.abs(n);
    let u = 0;
    while (v >= 1024 && u < units.length - 1) {
        v /= 1024;
        u++;
    }
    return `${n < 0 ? '-' : ''}${v >= 100 || u === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[u]}`;
}
export const JOB_STATES = ['queued', 'running', 'committing', 'retrying', 'completed', 'partial', 'failed', 'canceled', 'expired'];
export const ACTIVE_JOB_STATES = ['queued', 'running', 'committing', 'retrying'];
export const TERMINAL_JOB_STATES = ['completed', 'partial', 'failed', 'canceled', 'expired'];
export const RESTORE_STATES = ['queued', 'preparing', 'running', 'completed', 'partial', 'failed', 'canceled', 'expired'];
/** Agent command types. The agent executes only these; there is no remote shell. */
export const AGENT_COMMANDS = [
    'backup',
    'cancel',
    'restore',
    'verify',
    'preview_selection',
    'diagnostics',
    'rotate_credentials',
    'apply_policy',
    'update',
    'rekey',
];
//# sourceMappingURL=util.js.map